/* eslint-disable react/jsx-key */

import { StyledDivider } from '@/components/atoms/StyledDivider';
import { InstitutionDropdown } from '@/components/molecules/InstituteDropdown';
import { IntakeDropdown } from '@/components/molecules/IntakeDropdown';
import { DashboardPagination } from '@/components/molecules/Pagination';
import { Search } from '@/components/molecules/Search';
import { DefaultToolbar } from '@/components/molecules/Toolbar';
import { ApplicationState } from '@/store';
import { requestFilters } from '@/store/offers-dashboard/actions';
import { defaultTab } from '@/store/offers-dashboard/types';
import { getAllRegionConfigRecords } from '@/store/region_config_dashboard/actions';
import { QueryKeys } from '@/types';
import { getFilterGroups } from '@/utilities/getFilterGroups/getAdmissionsDashboardFilters';
import { hasPerm, P } from '@/utilities/roles/permissions';
import { ReactNode, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { ISystemAndUniConfig } from 'api-paths-cas';
import { Filters } from '../Filters';
import { getActiveFiltersFromParams } from '../Filters/helpers';
import { DataGridHeader } from '../DataGridHeader';
import { ExportApplicationsButton } from '@/components/atoms/ExportApplicationsButton/ExportApplicationsButton';

function useGetFilterOptions(uniConfig, pathway, activeTab): void {
  const dispatch = useDispatch();
  useEffect(() => {
    if (uniConfig && pathway) {
      dispatch(requestFilters());
      dispatch(getAllRegionConfigRecords(uniConfig.university_code, pathway));
    }
  }, [dispatch, uniConfig, pathway, activeTab]);
}

/**
 * Organism that combines the filters saga, the generic default toolbar molecule, search, filters,
 * intake, and institution generic molecules, into a specialised apply dashboard toolbar.
 */
export function ApplyToolbar(): ReactNode {
  const { uniConfig, regionConfigs } = useSelector((state: ApplicationState) => ({
    uniConfig: state.config as ISystemAndUniConfig,
    regionConfigs: state.regionConfigs,
  }));
  const { pathway, isPathway, userRole } = useSelector(({ auth }: ApplicationState) => ({
    pathway: auth.pathway,
    isPathway: auth.isPathway,
    userRole: auth.role,
  }));

  const { rows, filterOptionsLoaded, filterOptions, tabs } = useSelector(({ offersDashboard }: ApplicationState) => ({
    rows: offersDashboard.dashboardRows,
    filterOptionsLoaded: offersDashboard.filterOptionsLoaded,
    filters: offersDashboard.filters,
    filterOptions: offersDashboard.filterOptions,
    tabs: offersDashboard.tabs,
  }));

  const activeTab = useSelector(({ offersDashboard }: ApplicationState) => offersDashboard.currentTab);

  useGetFilterOptions(uniConfig, pathway, activeTab);

  const location = useLocation();

  const filterGroups = useMemo(() => {
    if (!uniConfig || !regionConfigs || !filterOptionsLoaded) return [];
    const activeFilters = getActiveFiltersFromParams(new URLSearchParams(location.search));
    return getFilterGroups({
      activeFilters,
      agents: undefined,
      regionConfigs,
      config: uniConfig,
      isPathway,
      programmableTags: undefined,
      userRole,
      rows,
      filterOptions,
    });
  }, [
    regionConfigs,
    uniConfig,
    userRole,
    isPathway,
    rows,
    filterOptionsLoaded,
    activeTab,
    filterOptions,
    location.search,
  ]);

  const canViewUniSelector = hasPerm(userRole, P.MULTI_UNIVERSITY_VIEW);

  const headerComponents = [
    <>
      <Search />
      <StyledDivider />
      <Filters filterGroups={filterGroups} filterOptionsLoading={!filterOptionsLoaded} />
      <StyledDivider />
      <ExportApplicationsButton />
    </>,
    <DashboardPagination
      showRangeSelector={true}
      defaultTab={defaultTab}
      queryKey={QueryKeys.CURRENT_TAB}
      tabs={tabs}
    />,
  ];

  const activeComponent = (
    <>
      {canViewUniSelector && <InstitutionDropdown />}
      <IntakeDropdown />
    </>
  );
  return (
    <DataGridHeader
      title={activeTab}
      activeComponent={activeComponent}
      toolbarComponent={<DefaultToolbar HeaderComponents={headerComponents} />}
    />
  );
}
